import React from 'react';
import {
  AgnesButton,
  AgnesDatePickerInput,
  AgnesPopover,
  AgnesPopoverContent,
  AgnesPopoverTrigger,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
} from '@agnes/ui';
import { Filter, Trash2, X } from 'lucide-react';
import { labelFiltro } from '@/lib/filtros';
import {
  type FiltrosClassificacaoAnalise,
  type OpcoesFiltro,
} from '@/stores/useClassificacaoAnaliseStore';

const TRIGGER_CLS =
  '[&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

const VAZIO: FiltrosClassificacaoAnalise = {
  grauRisco: '',
  alteradoPor: '',
  atualizacao: '',
};

const paraBR = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '');

type Props = {
  opcoes: OpcoesFiltro;
  onApply: (f: FiltrosClassificacaoAnalise) => void;
  count?: number;
};

export default function ClassificacaoAnaliseFilter({ opcoes, onApply, count = 0 }: Props) {
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState<FiltrosClassificacaoAnalise>(VAZIO);
  const [atualizacaoIso, setAtualizacaoIso] = React.useState('');
  const set = (k: keyof FiltrosClassificacaoAnalise) => (v: string) =>
    setF((s) => ({ ...s, [k]: v }));

  const aplicar = () => {
    onApply({ ...f, atualizacao: paraBR(atualizacaoIso) });
    setOpen(false);
  };
  const limpar = () => {
    setF(VAZIO);
    setAtualizacaoIso('');
    onApply(VAZIO);
  };

  const strSelect = (campo: 'grauRisco' | 'alteradoPor', label: string, options: string[]) => (
    <AgnesSelect value={f[campo]} onValueChange={set(campo)}>
      <AgnesSelectTrigger className={TRIGGER_CLS} data-qa={`classificacao-analise-filtro-${campo}`}>
        <AgnesSelectValue placeholder={label} />
      </AgnesSelectTrigger>
      <AgnesSelectContent>
        {options.map((o) => (
          <AgnesSelectItem key={o} value={o}>
            {o}
          </AgnesSelectItem>
        ))}
      </AgnesSelectContent>
    </AgnesSelect>
  );

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" />}
          data-qa="classificacao-analise-filtro"
          aria-label={count > 0 ? `Filtro, ${count} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', count)}
        </AgnesButton>
      </AgnesPopoverTrigger>

      <AgnesPopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto px-4 py-4">
          {strSelect('grauRisco', 'Grau de risco', opcoes.grauRisco)}

          <AgnesDatePickerInput
            value={atualizacaoIso}
            onChange={(value) => setAtualizacaoIso(value)}
            placeholder="Atualização"
            dataQa="classificacao-analise-filtro-atualizacao"
          />

          {strSelect('alteradoPor', 'Atualizado por', opcoes.alteradoPor)}
        </div>

        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton
            variant="primary"
            onClick={aplicar}
            className="w-full"
            data-qa="classificacao-analise-filtro-aplicar"
          >
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            startIcon={<Trash2 className="h-4 w-4" />}
            className="w-full"
            data-qa="classificacao-analise-filtro-limpar"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}
